import { Box, Button, Container, Typography } from "@mui/material";
import { animated, to as interpolate, useSprings } from "@react-spring/web";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import grpImage from "../assets/grp.jpg";
import loadedtank from "../assets/loadedtank.jpeg";
import pipe from "../assets/pipe.jpg";
import pipe1 from "../assets/pipe1.jpg";
import s from "../assets/s.jpeg";

const heroBg =
  "https://lh3.googleusercontent.com/gps-cs-s/AG0ilSzs5m5iUK3C2CdOwDf9aYccwSmuUQCDqwe4PRd1id7Y05M49wmf7MYqkOofJnKONavfzPUp8isa8JECnXlAATNm95OGbZW-k_8slD1kCImgL9ixoxy80tQKP5qRhjayKN2TNPVKbbkCDZQ=s1360-w1360-h1020-rw";

const images = [pipe, pipe1, s, loadedtank];

const brandBlue = "#1a237e";
const brandRed = "#d50000";

export default function HeroSection() {
  const len = images.length;
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // auto-advance
  useEffect(() => {
    const t = setInterval(() => {
      if (!pausedRef.current) setIndex((p) => (p + 1) % len);
    }, 3000);
    return () => clearInterval(t);
  }, [len]);

  // initial springs: set all off-screen left except index 0
  const [springs, api] = useSprings(len, (i) => ({
    x: i === 0 ? 0 : -100,
    scale: i === 0 ? 1 : 0.85,
    opacity: i === 0 ? 1 : 0.35,
    blur: i === 0 ? 0 : 4,
    config: { tension: 220, friction: 26 },
  }));

  // update springs on index change
  useEffect(() => {
    api.start((i) => {
      const prev = (index - 1 + len) % len;

      if (i === index) {
        // current in center
        return { x: 0, scale: 1, opacity: 1, blur: 0 };
      } else if (i === prev) {
        // previous half-visible on left
        return { x: -40, scale: 0.92, opacity: 0.5, blur: 4 };
      } else {
        // hidden to left
        return { x: -100, scale: 0.85, opacity: 0.25, blur: 6 };
      }
    });
  }, [index, api, len]);

  // optional: change index on click / next arrow etc.
  const goNext = () => setIndex((p) => (p + 1) % len);
  const goPrev = () => setIndex((p) => (p - 1 + len) % len);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "650px", md: "700px" },
        pt: { xs: "220px", sm: "230px", md: 0 },
        display: "flex",
        alignItems: "center",
        color: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${grpImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(4px)",
          transform: "scale(1.06)",
        }}
      />

      {/* overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to right, rgba(0,0,0,0.75), rgba(0,0,0,0.35))",
        }}
      />

      <Container sx={{ position: "relative", zIndex: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 6,
          }}
        >
          {/* LEFT */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                textTransform: "uppercase",
                letterSpacing: "2px",
                fontWeight: 900,
                mb: 2,
                color: "#e3f2fd",
                display: "inline-block",
                px: 2,
                py: 0.6,
                fontSize: "0.95rem",
                borderRadius: "4px",
                border: "1px solid rgba(255,255,255,0.4)",
                boxShadow: "0 0 14px rgba(26,35,126,0.9)",
                background: "rgba(26,35,126,0.45)",
                backdropFilter: "blur(5px)",
              }}
            >
              Strong, trustworthy, powerful.
            </Typography>

            {/* <Typography
              sx={{
                textTransform: "uppercase",
                letterSpacing: "2px",
                fontWeight: 800,
                mb: 2,
                color: "#fff",
                display: "inline-block",
                px: 2.4,
                py: 0.8,
                borderRadius: "30px",
                fontSize: "0.85rem",
                background: "rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,255,255,0.25)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
              }}
            >
              Premium Steel Engineering
            </Typography> */}

            {/* <Typography
              sx={{
                textTransform: "uppercase",
                letterSpacing: "2px",
                fontWeight: 700,
                mb: 2,
                color: "#fff",
                display: "inline-block",
                px: 2.2,
                py: 0.8,
                borderRadius: "6px",
                fontSize: "0.95rem",
                background:
                  "linear-gradient(90deg, rgba(26,35,126,0.85), rgba(26,35,126,0.55))",
                backdropFilter: "blur(6px)",
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              }}
            >
              Premium Steel Engineering
            </Typography> */}

            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: "2rem", md: "3rem" }, // smaller & cleaner
                lineHeight: 1.22,
                mb: 2,
                textShadow: "0 4px 12px rgba(0,0,0,0.45)",
              }}
            >
              Strength in Every Structure. Trust in Every Detail.
            </Typography>

            <Typography
              sx={{
                color: "#e6e6e6",
                fontSize: { xs: "0.95rem", md: "1.15rem" }, // slightly smaller
                mb: 3.5,
                maxWidth: "480px",
                lineHeight: 1.55,
                opacity: 0.92,
              }}
            >
              Acoustic enclosures, industrial fuel tanks, modular structures,
              and custom metal fabrication.
            </Typography>

            <Button
              onClick={() => navigate("/get-quote")}
              // component={RouterLink}
              // to="/get-quote"
              sx={{
                px: 4,
                py: 1.3,
                fontSize: "1.05rem",
                fontWeight: 800,
                textTransform: "none",
                borderRadius: "6px",

                // Glass UI + Glow (same as label)
                color: "#fff",
                background: "rgba(213, 0, 0, 0.45)",
                border: "1px solid rgba(255,255,255,0.35)",
                boxShadow: "0 0 14px rgba(213,0,0,0.7)",
                backdropFilter: "blur(6px)",

                display: "flex",
                alignItems: "center",
                gap: 1.2,
                transition: "all 0.32s ease",

                "&:hover": {
                  background: "rgba(213, 0, 0, 0.75)",
                  boxShadow: "0 0 18px rgba(213,0,0,0.9)",
                  transform: "translateY(-2px)",
                  borderColor: "rgba(255,255,255,0.55)",
                },

                "& span.icon": {
                  transition: "transform 0.32s ease",
                },
                "&:hover span.icon": {
                  transform: "translateX(5px)",
                },
              }}
            >
              Request a Quote
              <span className="icon" style={{ fontSize: "1.2rem" }}>
                ➜
              </span>
            </Button>

            {/* <Button
              variant="contained"
              sx={{
                backgroundColor: brandRed,
                px: 4,
                py: 1.4,
                fontSize: "1.05rem",
                borderRadius: "10px",
                fontWeight: 700,
                gap: 1.5,
                textTransform: "none",
                boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                "&:hover": { backgroundColor: "#b40000" },
              }}
            >
              Request a Quote{" "}
              <span
                style={{
                  display: "inline-block",
                  transform: "translateY(1px)",
                  fontSize: "1.2rem",
                }}
              >
                ➜
              </span>
            </Button> */}
          </Box>

          {/* RIGHT — slider */}
          <Box
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            sx={{
              position: "relative",
              width: "460px",
              height: "400px",
              overflow: "hidden",
              borderRadius: "20px",
              flexShrink: 0,
            }}
          >
            {springs.map((anim, i) => {
              const isPrev = i === (index - 1 + len) % len;
              const z = i === index ? 10 : isPrev ? 5 : 1;
              return (
                <animated.div
                  key={i}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: 20,
                    backgroundImage: `url(${images[i]})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                    // transform combines translateX (%) with scale
                    transform: interpolate(
                      [anim.x, anim.scale],
                      (x, s) => `translateX(${x}%) scale(${s})`
                    ),
                    opacity: anim.opacity,
                    filter: anim.blur.to((b) => `blur(${b}px)`),
                    zIndex: z,
                    willChange: "transform, opacity, filter",
                    transition: "z-index 0s",
                  }}
                />
              );
            })}

            {/* Optional simple controls (small, unobtrusive) */}
            {/* <Box
              sx={{
                position: "absolute",
                bottom: 12,
                left: 12,
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={goPrev}
                sx={{
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                }}
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={goNext}
                sx={{
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                }}
              >
                Next
              </Button>
            </Box> */}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
